import React, { useEffect } from "react";
import Assets from "imports/assets.import";
import { PrimaryButton } from "utils/imports.utils";
import { ButtonTypes, ObjectTypes, ObjectTypesImages, ProjectStatus, PropertiesTypes, TargetImageData, TargetImageObject } from "utils/interface.utils";
import { getObjectDisplayName, getObjectTypeImage, getObjectTypeLabel, hasObjectOfType, isUnlimitedUser, toastifyError, useSetState } from "utils/functions.utils";
import { STRINGS } from "constants/strings.constant";
import object from "models/object.model";
import auth from "models/auth.model";
import ChooseObjectsCard from "components/choose_objects_card/choose_objects_card.component";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { addTargetImage, setSelectedTargetIndex, setSelectedObjectID, addObjectsToTargetImage, setSelectedTargetImageID, deleteObjectOfTargetImage, setCurrentSelectedPropertyType, deleteTargetImage, updateTargetImage, setIsTargetImageListItemClicked, setIsObjectListItemClicked, setLastDeletedTargetImageData, setShouldUpdateObjectState, setTargetImageSelection, selectObjectKeepingTool } from "store/reducers/editorSlice";
import ReactDragListView from "react-drag-listview";
import { nanoid } from "@reduxjs/toolkit";
import "./add_dropdown.component.scss";
interface IAddDropdownProps {
    openProperties: (isOpen: boolean) => void
    projectPaymentStatus: boolean
    projectStatus: string
    // Called when the Objects "+" is clicked, so the editor can show its "Make this picture pop" card first.
    // Without it the "+" opens the object chooser directly.
    onAddObjectClick?: () => void
    // Bumped by the editor each time that card's "Add object" asks for the object chooser; 0 means no request yet.
    addObjectRequestCount?: number
}

const AddDropdown = (props: IAddDropdownProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { targetImageDataList, selectedTargetIndex, selectedTargetImageID, selectedObjectID, currentSelectedPropertyType } = useSelector((state: RootState) => state.editor)

    const [state, setState] = useSetState({
        currentSelectedObjectID: null,
        targetImages: null,
        isChooseObjectsCardOpen: false,
    })

    const sidebarStrings = STRINGS.editor.sidebar;
    const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];
    const currentObjects: TargetImageObject[] = currentTargetImage?.objects ?? [];
    const canEditTargets = !props.projectPaymentStatus && props.projectStatus !== ProjectStatus.PUBLISHED;

    useEffect(() => {
        if (props.addObjectRequestCount) setState({ isChooseObjectsCardOpen: true });
    }, [props.addObjectRequestCount])

    const displayProperties = () => {
        const targetImageID = nanoid();
        dispatch(setCurrentSelectedPropertyType(PropertiesTypes.TARGET));
        dispatch(addTargetImage({
            id: targetImageID,
            targetImage: null,
            targetId: null,
            containedTargetImageDimension: { width: 0, height: 0 },
            selectedTargetImageDimension: { width: 0, height: 0 },
            objects: []
        }));
        dispatch(setSelectedTargetIndex(targetImageDataList.length));
        dispatch(setSelectedTargetImageID(targetImageID));
        props.openProperties(true);
    }

    const displayObjectProperties = (type: PropertiesTypes) => {
        // If the type is undefined, return
        if (!type) return;

        // One video per target (images are unlimited), except for the exempt account.
        if (!isUnlimitedUser() && type === PropertiesTypes.VIDEO) {
            const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];
            if (hasObjectOfType(currentTargetImage?.objects ?? [], ObjectTypes.VIDEO)) {
                toastifyError('This target already has a video object. Delete it first to add another.');
                return;
            }
        }

        const objectID = nanoid();
        
        if (type !== currentSelectedPropertyType) dispatch(setCurrentSelectedPropertyType(type));
        if (objectID !== selectedObjectID) dispatch(setSelectedObjectID(objectID));
        
        dispatch(setShouldUpdateObjectState(true));
        
        setState({ isChooseObjectsCardOpen: false })
        props.openProperties(true);
    }

    const handleTargetImageListItemClick = (targetImage: TargetImageData, index: number) => {
        // Selects the target itself, so Properties shows the target's own data (image, Replace
        // Image, lock state) even when it has objects; those are opened from the Objects list.
        dispatch(setTargetImageSelection({
            selectedObjectID: null,
            currentSelectedPropertyType: PropertiesTypes.TARGET,
            selectedTargetIndex: index,
            selectedTargetImageID: targetImage.id,
            isTargetImageListItemClicked: true,
            shouldUpdateObjectState: true
        }));
        props.openProperties(true);
    }

    const handleTargetImageDelete = (targetImageID: string) => {
        const getDeletingTargetImageData = targetImageDataList.find((targetImage) => targetImage.id === targetImageID);
        dispatch(setLastDeletedTargetImageData(getDeletingTargetImageData ?? null));
        dispatch(deleteTargetImage(targetImageID));
    }

    const handleObjectListItemClick = (object: TargetImageObject) => {
        dispatch(selectObjectKeepingTool({ objectID: object.id, objectType: object.type }));
        dispatch(setIsObjectListItemClicked(true));
        dispatch(setShouldUpdateObjectState(true))
    }

    const handleDeleteObject = async (event: React.MouseEvent<HTMLDivElement>, objectID: string) => {
        event.stopPropagation();

        const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];
        const objectToDelete = currentTargetImage?.objects.find((obj) => obj.id === objectID);
        const serverObjectId = objectToDelete?.properties?.objectId;

        if (serverObjectId) {
            try {
                await object.deleteObject(serverObjectId);
            } catch (error: any) {
                toastifyError(error?.response?.data?.message || "Failed to delete object");
                return;
            }
        }

        if (currentTargetImage?.projectDataId) {
            try {
                await auth.editProjectData({
                    project_data_id: currentTargetImage.projectDataId,
                    data: { ...currentTargetImage, objects: currentTargetImage.objects.filter((obj) => obj.id !== objectID) },
                });
            } catch (error: any) {
                toastifyError(error?.response?.data?.message || "Failed to save object deletion");
            }
        }

        dispatch(deleteObjectOfTargetImage(objectID));
        // deleteObjectOfTargetImage auto-selects the last remaining object as a side effect.
        // Without this, Properties keeps its local per-type state from whatever was selected
        // before the delete (e.g. the object just removed) and — since its sync-back effect
        // reacts to selectedObjectID changing regardless of source — overwrites the newly
        // auto-selected (untouched) object's real properties with that stale local state,
        // making it collapse into a duplicate of the deleted object while staying correctly
        // listed. setShouldUpdateObjectState(true) forces Properties to first reload its local
        // state from the actual selected object (see its effect gated on shouldUpdateObjectState).
        dispatch(setShouldUpdateObjectState(true));
    }

    const handleOnDragEnd = (from: number, to: number) => {
        if (selectedTargetIndex === null || selectedTargetIndex === undefined) return;

        const currentTargetImage = targetImageDataList[selectedTargetIndex];
        if (!currentTargetImage?.objects || currentTargetImage.objects.length <= 1) return;

        // Create a deep copy of the objects array with new object references
        const newObjects = Array.from(currentTargetImage.objects);

        newObjects[from] = {
            ...newObjects[from],
            properties: {
                ...newObjects[from].properties,
                renderOrder: currentTargetImage.objects[to].properties.renderOrder
            }
        };

        newObjects[to] = {
            ...newObjects[to],
            properties: {
                ...newObjects[to].properties,
                renderOrder: currentTargetImage.objects[from].properties.renderOrder
            }
        };

        // // Swap the objects
        const fromTempObject = newObjects[from];
        newObjects[from] = newObjects[to];
        newObjects[to] = fromTempObject;

        dispatch(updateTargetImage({
            id: currentTargetImage.id,
            targetImage: currentTargetImage.targetImage,
            targetId: currentTargetImage.targetId,
            selectedTargetImageDimension: currentTargetImage.selectedTargetImageDimension,
            containedTargetImageDimension: currentTargetImage.containedTargetImageDimension,
            objects: newObjects
        }))
    }



    const renderTargetThumbnail = (targetImage: string | null) => (
        <div className="add_dropdown_item_body_list_thumbnail">
            {targetImage ? <img src={targetImage} alt="" className="add_dropdown_item_body_list_thumbnail_image" /> : <img src={ObjectTypesImages[ObjectTypes.IMAGE]} alt="" className="add_dropdown_item_body_list_thumbnail_icon" />}
        </div>
    )

    // Image objects show their own picture, video objects a still frame of their video (never playing);
    // every other type shows its type icon.
    const renderObjectThumbnail = (object: TargetImageObject) => {
        const src = object.type === ObjectTypes.IMAGE || object.type === ObjectTypes.VIDEO ? object.properties?.src : null;
        let media = <img src={getObjectTypeImage(object.type)} alt="" className="add_dropdown_item_body_list_thumbnail_icon" />;
        if (src && object.type === ObjectTypes.VIDEO) media = <video src={`${src}#t=0.1`} muted preload="metadata" className="add_dropdown_item_body_list_thumbnail_image" />;
        else if (src) media = <img src={src} alt="" className="add_dropdown_item_body_list_thumbnail_image" />;
        return <div className="add_dropdown_item_body_list_thumbnail">{media}</div>;
    }

    return (
        <div className="add_dropdown">
            <div className="add_dropdown_container">
                <div className="add_dropdown_item">
                    <div className="add_dropdown_item_header">
                        <div className="add_dropdown_item_header_name">{sidebarStrings.targetImageTitle}</div>
                        {
                            (canEditTargets && targetImageDataList?.length > 0) &&
                            <div className="add_dropdown_item_header_button_container">
                                <PrimaryButton
                                    left_icon={Assets.plus}
                                    iconSize={{ width: 20, height: 20 }}
                                    type={ButtonTypes.PRIMARY_GRADIENT}
                                    onClick={displayProperties}
                                />
                            </div>
                        }
                    </div>
                    {
                        (canEditTargets && !(targetImageDataList?.length > 0)) &&
                        <button type="button" className="add_dropdown_add_target" onClick={displayProperties}>
                            <span className="add_dropdown_add_target_icon"><img src={Assets.plus} alt="" /></span>
                            <span className="add_dropdown_add_target_text">
                                <span className="add_dropdown_add_target_title">{sidebarStrings.addTargetImage}</span>
                                <span className="add_dropdown_add_target_hint">{sidebarStrings.addTargetImageHint}</span>
                            </span>
                        </button>
                    }
                    {
                        targetImageDataList?.length > 0 &&
                        <div className="add_dropdown_item_body_active">
                            {
                                targetImageDataList?.map((item: TargetImageData, index: number) => {
                                    // The target the user is working in always gets the lime border, whether or not one of its objects is selected.
                                    const isActiveTarget = selectedTargetImageID === item.id;
                                    return (
                                        <div
                                            className={`add_dropdown_item_body_list_active${isActiveTarget ? " add_dropdown_item_body_list_active_target" : ""}`}
                                            aria-current={isActiveTarget ? "true" : undefined}
                                            key={item.id}
                                            onClick={() => { handleTargetImageListItemClick(item, index) }}
                                        >
                                            <div className="add_dropdown_item_body_list_left_container">
                                                {renderTargetThumbnail(item.targetImage)}
                                                <div className="add_dropdown_item_body_list_text">
                                                    <div className="add_dropdown_item_body_list_name">
                                                        {`image(${index + 1})`}
                                                    </div>
                                                    <div className="add_dropdown_item_body_list_kind">{sidebarStrings.targetKind}</div>
                                                </div>
                                            </div>
                                            {
                                                canEditTargets &&
                                                <div className="add_dropdown_item_body_list_button_container" onClick={(e) => { e.stopPropagation(); handleTargetImageDelete(item.id) }}>
                                                    <img src={Assets.trash} alt={sidebarStrings.deleteIconAlt} />
                                                </div>
                                            }
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                </div>
                <div className="add_dropdown_item add_dropdown_item_objects">
                    <div className="add_dropdown_item_header">
                        <div className="add_dropdown_item_header_name">
                            {sidebarStrings.objectsTitle}
                            {currentTargetImage?.targetImage && <span className="add_dropdown_item_header_count">{currentObjects.length}</span>}
                        </div>
                        <div className="add_dropdown_item_header_button_container">
                            <PrimaryButton
                                onClick={() => props.onAddObjectClick ? props.onAddObjectClick() : setState({ isChooseObjectsCardOpen: true })}
                                left_icon={Assets.plus}
                                iconSize={{ width: 20, height: 20 }}
                                type={ButtonTypes.PRIMARY_GRADIENT}
                                disabled={!currentTargetImage?.targetImage}
                            />
                        </div>
                    </div>
                    {
                        !currentTargetImage?.targetImage &&
                        <div className="add_dropdown_note add_dropdown_note_locked">
                            <img src={Assets.lock} alt="" className="add_dropdown_note_icon" />
                            <span>{sidebarStrings.needTargetFirst}</span>
                        </div>
                    }
                    {
                        (currentTargetImage?.targetImage && currentObjects.length === 0) &&
                        <div className="add_dropdown_empty_objects">
                            <div className="add_dropdown_empty_objects_title">{sidebarStrings.noObjectsTitle}</div>
                            <div className="add_dropdown_empty_objects_text">{sidebarStrings.noObjectsText}</div>
                        </div>
                    }
                    {
                        currentObjects.length > 0 &&
                        <ReactDragListView
                            handleSelector=".add_dropdown_drag_handle"
                            nodeSelector="li"
                            onDragEnd={(from, to) => handleOnDragEnd(from, to)}
                        >
                            <div className="add_dropdown_item_body_active add_dropdown_item_body_active_objects">
                                {
                                    currentObjects.map((object: TargetImageObject, index: number) => {
                                        const isSelected = selectedObjectID === object.id;
                                        return (
                                            <li
                                                className={`add_dropdown_item_body_list_active add_dropdown_item_body_list_objects${isSelected ? " add_dropdown_item_body_list_selected" : ""}`}
                                                key={object.id}
                                                onClick={() => handleObjectListItemClick(object)}
                                            >
                                                <div className="add_dropdown_item_body_list_objects_left_container">
                                                    <div className="add_dropdown_drag_handle">
                                                        <img src={Assets.drag} alt={sidebarStrings.dragIconAlt} />
                                                    </div>
                                                    {renderObjectThumbnail(object)}
                                                    <div className="add_dropdown_item_body_list_text">
                                                        <div className="add_dropdown_item_body_list_name">
                                                            {getObjectDisplayName(currentObjects, index)}
                                                        </div>
                                                        <div className="add_dropdown_item_body_list_kind">{getObjectTypeLabel(object.type)}</div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="add_dropdown_item_body_list_button_container" onClick={(event) => handleDeleteObject(event, object.id)}>
                                                        <img src={Assets.trash} alt={sidebarStrings.deleteIconAlt} />
                                                    </div>
                                                </div>
                                            </li>
                                        )
                                    })
                                }
                            </div>
                        </ReactDragListView>
                    }
                    {
                        currentObjects.length > 1 &&
                        <div className="add_dropdown_hint">{sidebarStrings.reorderHint}</div>
                    }
                </div>
                <div className="add_dropdown_choose_objects_card_container">
                    <ChooseObjectsCard
                        isOpen={state.isChooseObjectsCardOpen}
                        onClose={() => { setState({ isChooseObjectsCardOpen: false }) }}
                        onSelectedObject={(type: PropertiesTypes) => {
                            displayObjectProperties(type)
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

export default AddDropdown;
